import {
  afterNextRender,
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgClass } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SharedModule } from '../../shared/shared.module';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalizedUrlService } from '../../core/services/localized-url.service';
import { RouteKey } from '../../core/i18n/route-slugs';

interface DropdownItem {
  label: string;
  description: string;
  routeKey?: RouteKey;
  route?: string;
}

@Component({
  selector: 'app-dropdown-menu',
  template: `
    <div class="flex flex-col items-start xl:relative" (mouseenter)="isHoverDevice() && isOpen.set(true)" (mouseleave)="isHoverDevice() && isOpen.set(false)">
      <!-- Bouton qui ouvre le menu -->
      <app-link-highlight
        [forceActive]="(isDropdownPageActive$ | async) || isOpen()"
        (click)="toggleMenu()"
      >
        {{ 'nav.public.rent-my-room' | transloco }}
        <i
          class="far fa-chevron-down transition-transform duration-300"
          [ngClass]="{ 'rotate-180': isOpen() }"
        ></i>
      </app-link-highlight>

      <!-- Menu déroulant desktop (flottant) -->
      @if (isOpen()) {
        <div
          @slideDown
          class="hidden xl:block absolute top-full left-1/2 -translate-x-1/2 z-50 w-72 pt-4"
        >
          <div class="rounded-2xl bg-white shadow-xl ring-1 ring-black/5 overflow-hidden">
            <div class="py-2">
              @for (item of items; track item.label) {
                <a
                  [routerLink]="resolveLink(item)"
                  (click)="toggleMenu()"
                  class="group flex flex-col px-4 py-3 no-underline hover:bg-gray-50 transition-colors"
                >
                  <span class="text-sm font-medium text-secondary-900">{{ item.label | transloco }}</span>
                  <span class="mt-0.5 text-xs text-gray-400">{{ item.description | transloco }}</span>
                </a>
              }
            </div>
          </div>
        </div>
      }

      <!-- Menu intégré mobile (dans le flux) -->
      @if (isOpen()) {
        <div @slideDown class="xl:hidden mt-3 flex flex-col items-start gap-1.5 pl-4">
          @for (item of items; track item.label) {
            <a
              [routerLink]="resolveLink(item)"
              routerLinkActive="!underline underline-offset-4"
              [routerLinkActiveOptions]="{ exact: true }"
              (click)="toggleMenu()"
              class="text-sm font-medium text-secondary-900 no-underline rounded-full px-4 py-2 transition-colors hover:bg-gray-200"
            >{{ item.label | transloco }}</a>
          }
        </div>
      }
    </div>
  `,
  imports: [NgClass, SharedModule, AsyncPipe, RouterLink, RouterLinkActive],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class DropdownMenuComponent {
  private router = inject(Router);
  private localizedUrl = inject(LocalizedUrlService);

  items: DropdownItem[] = [
    { label: 'nav.public.dropdown.discover', description: 'nav.public.dropdown.discover.description', routeKey: 'about' },
    { label: 'nav.public.dropdown.pricing', description: 'nav.public.dropdown.pricing.description', routeKey: 'pricing' },
    { label: 'nav.public.dropdown.become-host', description: 'nav.public.dropdown.become-host.description', routeKey: 'becomeHost' },
    { label: 'nav.public.dropdown.partners-resellers', description: 'nav.public.dropdown.partners-resellers.description', routeKey: 'partners' },
    { label: 'nav.public.dropdown.contact', description: 'nav.public.dropdown.contact.description', routeKey: 'contact' },
  ];

  isOpen = signal(this.isDropdownPageActive());
  isHoverDevice = signal(false);

  constructor(private elementRef: ElementRef) {
    afterNextRender(() => {
      this.isHoverDevice.set(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    });
  }

  /** Résout les segments de lien d'un item : clé localisée ou route brute (auth). */
  resolveLink(item: DropdownItem): string | string[] {
    return item.routeKey ? this.localizedUrl.link(item.routeKey) : item.route!;
  }

  /** Vrai si l'URL courante correspond à l'une des pages du menu déroulant. */
  private isDropdownPageActive(): boolean {
    const current = this.router.url.split('?')[0].split('#')[0];
    return this.items.some((item) => {
      const link = this.resolveLink(item);
      const path = Array.isArray(link) ? link.join('/') : link;
      return current === path;
    });
  }

  isDropdownPageActive$: Observable<boolean> = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => this.isDropdownPageActive()),
  );

  toggleMenu() {
    this.isOpen.set(!this.isOpen());
  }

  // Ferme le menu si on clique ailleurs
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }
}
